<template>
  <!-- O item novo com campo de acesso por fórmula espera a prévia: à vista antes dela, o campo oculto
       aparecia e sumia (relato do usuário) — ver o `waiting` -->
  <ontos-dialog-loading v-if="waiting"/>
  <v-row v-else>
<!--    <v-col cols="12">-->
<!--      {{ instance }}-->
<!--    </v-col>-->
    <v-col cols="12" :sm="colsForProperty(property, 6, 6) " :lg="colsForProperty(property, '6', '4')"
           v-for="property in idea.properties.filter(p => fieldVisible(instance, p) && !omitProperties.includes(p.alias))"
           :key="property.alias" :class="{'flex-grow-1': growsForProperty(property)}"
           :style="sizeForProperty(property)"
    >
      <ontos-instance-property-input :value="instance[property.alias]" :property="property"
                                     :domain="property.relationship? domains[property.relationship.idea] : undefined"
                                     :options="choiceOptions(property)"
                                     :allowed="allowed[property.alias]"
                                     :reverse-info="reverseInfo && reverseInfo[property.alias]"
                                     :idea-alias="idea.alias"
                                     :instance-id="context.instance ? Number(context.instance) : null"
                                     :ref="`property-${property.alias}`"
                                     :readonly="!fieldEditable(instance, property) || computingNow(instance, property)
                                         || (property.alias in fixedValues && !property.multiple)"
                                     :fixed="property.multiple ? $arrays.asArray(fixedValues[property.alias]) : []"
                                     @input="instance[property.alias] = $event; touched.add(property.alias); validateOnInput(property, $event);"
                                     @change="instance[property.alias] = $event; validate(property, $event); askServer(property.alias);"
                                     @keydown.enter="focusNext"
                                     :error-message="messageOf(property.alias)"
                                     :warnings="warningsOf(property.alias)"
                                     :pending="pending.includes(property.alias)"
                                     :item="instance"
                                     :required="isRequired(instance, property)"
                                     :unique="property.unique"
                                     :system-alias="context.system"
                                     @showMessage="$emit('showMessage', $event)"
      />
    </v-col>
    <!-- $set e não v-model: `_state` costuma ser chave nova na instância, e a
         atribuição crua do v-model não seria observada — o campo desenharia o valor
         uma vez e depois ficaria preso nele. -->
    <!-- Sem `dense`: nenhum outro campo do formulário passa densidade, e o compact é mais
         baixo — era ele que deixava a situação mais alta que os vizinhos. A densidade
         compacta continua valendo onde a linha toda é compacta, no filtro da listagem. -->
    <v-col v-if="workflow" cols="12" sm="6" lg="4" class="flex-grow-1">
      <ontos-instance-state-input :value="instance._state" :workflow="workflow"
                                  @input="instance._state = $event; askServer('_state')"/>
    </v-col>
  </v-row>
</template>
<script>
import {fieldEditable, fieldVisible, hasFieldAccess} from '@/types/Idea'
import OntosInstancePropertyInput from "@/components/live/instance/InstancePropertyInput";
import OntosInstanceStateInput from "@/components/live/instance/InstanceStateInput.vue";
import DialogLoading from "@/components/shared/DialogLoading.vue";
import {CREATE_BUTTON_CHARS} from "@/components/shared/CreatableAutocomplete.vue";

//O campo de busca do `v-autocomplete` ao lado do valor escolhido, em `ch` — ver o `sizeForProperty`
const SEARCH_CHARS = 7

import {isMediaType} from "@/utils/mediaTypes";
import {defaultedProperties, isRequired, seedDefaults, seedState} from "@/utils/ontosUtils";
import {applyComputing, computingNow, defaultFormulaOf} from "@/types/IdeaProperty";
import {failuresAt, isStored, valueFailures} from "@/utils/fieldValidation";
import {failureText} from "@/utils/saveErrors";
import {refuse} from "@/utils/refusal";
import {asArray, asElement} from "@/utils/arrayUtils";
import {displayKind, relatedId} from "@/utils/propertyDisplay";
import {travelsAsList} from "@/utils/propertyDefinition";
import {domainOfProperty} from "@/types/Domain";
import {enhanceWorkflow} from "@/types/Workflow";
import {deepCopy} from "@/utils/copy";

export default {
  name: 'ontos-instance-input',
  components: {
    OntosInstancePropertyInput,
    OntosInstanceStateInput,
    'ontos-dialog-loading': DialogLoading
  },

  props: {
    idea: {type: Object, required: true},
    instance: {type: Object, required: true},
    domains: {
      type: Object, default: () => {
      }
    },
    context: {type: Object, required: true},
    //Os domínios dos atributos de seleção. O `domains` acima, apesar do nome, é outra
    //coisa: os itens candidatos de cada relacionamento, por coleção de destino.
    choiceDomains: {type: Array, default: () => []},
    //metadados das propriedades reversas, por alias (vem junto com relDomains)
    reverseInfo: {type: Object, default: () => ({})},
    //aliases que não devem ser exibidos nem validados (ex.: o campo dono numa
    //criação pendente, que só será preenchido depois que a outra ponta existir)
    omitProperties: {type: Array, default: () => []},
    //valores que não podem sair, por alias — ver o `fixedValues` do InstanceCreate
    fixedValues: {type: Object, default: () => ({})},
    //O item como começou — o `originalInstance` de quem hospeda: para onde volta o campo que a prévia
    //esconde (ver o `discardHidden`)
    original: {type: Object, default: () => ({})}
  },

  data() {
    return {
      //O que o cliente confere sozinho (`validate`)
      errorMessages: {},
      //As falhas de campo da recusa do salvar (`refuse`), até mudar algo de que dependem — ver o
      //`forgetRefusal`. À parte pelo mesmo motivo do `serverMessages`
      refusedMessages: {},
      //O que só o servidor sabe — o único e as regras —, da última prévia (`askServer`). À parte:
      //conferir um campo ao digitar não pode apagar o que o servidor disse dele, nem o contrário
      serverMessages: {},
      //Os campos mexidos nesta edição — ao digitar ou ao escolher: vão na prévia, decidem o que dela
      //se mostra e são os que o salvar confere numa edição (a catraca)
      touched: new Set(),
      //Por campo de escolha com regra, os ids que passariam se escolhidos — da última prévia
      allowed: {},
      //O número do pedido de prévia em curso — ver o `askServer`
      previewTicket: 0,
      //Os campos esperam a primeira prévia — ver o `start`
      waiting: false,
      //Os campos travados até a prévia em curso responder — ver o `affectedBy`
      pending: [],
      //O valor de cada campo como a última prévia o viu — ver o `remember`
      asked: {},
      //Os campos que ainda seguem o padrão por fórmula: a prévia o põe e o refaz, até alguém mexer
      //neles — ver o `start` e o `askServer`
      defaulted: []
    }
  },

  computed: {
    /**
     * O fluxo sai da própria coleção, e não de uma prop.
     *
     * Como prop, cada um dos quatro hospedeiros — as duas páginas e os dois modais
     * de criar/editar — tinha de se lembrar de passá-la, e três não passavam: o
     * campo de situação simplesmente não existia ao editar. O `_workflow` já vem
     * resolvido na idea, e é o mesmo que a listagem usa para filtrar por situação.
     *
     * Enriquecido aqui: o campo de situação precisa da cor de cada uma, que pode ser
     * escolhida ou automática, e quem resolve as duas é o fluxo enriquecido. O que vem na
     * idea é o documento cru do servidor.
     */
    workflow() {
      return this.idea._workflow ? enhanceWorkflow(this.idea._workflow) : null
    },

    /**
     * O maior identificador de cada domínio, em caracteres.
     *
     * É o que permite dar TETO ao select de relacionamento: a lista de opções é
     * conhecida, então dá para dimensionar o campo pelo maior nome que ele algum dia
     * vai mostrar, em vez de deixá-lo ocupar a fração de grade inteira. "Gênero" num
     * acervo de música não precisa de meia janela.
     *
     * Computed, e não conta no template: percorrer o domínio a cada redesenho custaria
     * caro num domínio grande, e ele só muda quando o `domains` muda.
     */
    domainWidths() {
      const widths = {}
      for (const alias of Object.keys(this.domains || {})) {
        widths[alias] = (this.domains[alias] || []).reduce(
            (max, item) => Math.max(max, String(item._identifier || '').length), 0)
      }
      return widths
    },

    /**
     * Os campos de que alguma calculada da coleção depende — a união dos `dependsOn`, que o
     * servidor deriva das fórmulas. Inclui `_state` quando uma fórmula lê a situação.
     */
    formulaInputs() {
      return new Set(this.idea.properties.flatMap(property => property.dependsOn || []))
    },

    /**
     * Os campos únicos, cuja conferência é do servidor — ver `askServer`. Mídia fica fora, como lá:
     * o valor é um endereço que muda a cada envio.
     */
    uniqueFields() {
      return new Set(this.idea.properties
          .filter(property => property.unique && isStored(property) && !isMediaType(property))
          .map(property => property.alias))
    },

    /** Os campos que alguma regra lê — o `dependsOn` de cada uma, que o servidor deriva da fórmula. */
    ruleInputs() {
      return new Set(this.idea.properties.flatMap(property =>
          (property.rules || []).flatMap(rule => rule.dependsOn || [])))
    },

    /**
     * Os campos com "Opções permitidas" — relacionamento, direto ou reverso, e seleção —, cujas opções
     * o servidor filtra para só oferecer o que passa (`allowed`, da prévia).
     */
    filteredFields() {
      return new Set(this.idea.properties
          .filter(property => property.optionFilter && !property.formula)
          .map(property => property.alias))
    },

    /** Os campos que algum filtro lê (`self.estado == estado`): mudá-los muda o que se oferece. */
    filterInputs() {
      return new Set(this.idea.properties.flatMap(property => property.optionFilterDependsOn || []))
    },

    /** Os campos com "Obrigatório quando", cuja fórmula é o servidor que avalia (`_required`, da prévia). */
    conditionallyRequired() {
      return this.idea.properties.filter(property => property.requiredWhen)
    },

    /** Os campos que algum "Obrigatório quando" lê: mudá-los pode pôr ou tirar o asterisco de outro. */
    requiredInputs() {
      return new Set(this.conditionallyRequired.flatMap(property => property.requiredDependsOn || []))
    },

    /** Algum campo é visível ou editável por fórmula: o que se mexe pode esconder ou travar outro. */
    fieldAccess() {
      return hasFieldAccess(this.idea)
    }
  },

  created() {
    this.start()
  },

  watch: {
    //O "Salvar e criar outro" troca o item sem remontar o formulário: sem isto, o item seguinte nascia
    //sem a prévia — sem as calculadas e com o campo oculto à vista até sair de um campo (relato do usuário)
    instance() {
      this.touched = new Set()
      this.start()
    }
  },

  methods: {
    /** O item que o formulário recebe: o que se semeia nele, e a prévia do que só o servidor sabe. */
    start() {
      //Se a situação estiver em branco, começa na inicial do fluxo
      seedState(this.instance, this.idea)

      //E os valores padrão. Quem decide o quanto semear é o CONTEXTO: sem item nele é criação, e o
      //padrão é o ponto de partida de todo campo; com item é edição de algo que já existe, e aí só o
      //obrigatório — ver `defaultedProperties`. O fixo se semeia aqui; o por fórmula vem da prévia, que
      //o refaz enquanto ninguém mexer no campo (pedido do usuário).
      //
      //Aqui, e não em cada tela, porque as duas de criar passam por este campo: a janela e a rota,
      //que ainda tem formulário próprio.
      const onlyRequired = !!this.context.instance
      seedDefaults(this.instance, this.idea, this.choiceDomains, {onlyRequired, candidates: this.domains})
      this.defaulted = defaultedProperties(this.instance, this.idea, {onlyRequired})
          .filter(property => defaultFormulaOf(property))
          .map(property => property.alias)

      //O relacionamento calculado não tem campo gravado, como o direto tem a lista de ids: o valor
      //dele chega no `_rels` da leitura, e é dali que o campo somente leitura tira os itens. As
      //alterações não o levam ao servidor — o EditInstanceService deixa de fora o que tem fórmula
      for (const property of this.idea.properties) {
        if (property.relationship && property.formula && this.instance._rels) {
          this.instance[property.alias] = this.instance._rels[property.alias]
        }
      }

      //Item NOVO nasce sem as calculadas: ninguém as leu do servidor ainda. Pedir a prévia já ao
      //abrir é o que faz o formulário mostrá-las com os defaults semeados acima. Item que já existe
      //chega com elas, da leitura — mas pergunta também se tem campo com opções permitidas, para a
      //lista dele já nascer com o que passa.
      //
      //O item novo com campo de acesso por fórmula ESPERA essa prévia para mostrar os campos: só ela
      //diz o que nasce oculto ou travado, e à vista antes dela o campo oculto aparecia e sumia
      //(relato do usuário). O que já existe não espera: chega com o `_access` da leitura. O padrão por
      //fórmula também espera: sem isso, o campo nasceria vazio e se encheria depois
      this.waiting = !this.context.instance && (this.fieldAccess || this.defaulted.length > 0)
      if (!this.waiting) this.shown()
      this.remember()
      if (!this.context.instance || this.filteredFields.size || this.defaulted.length) this.askServer(undefined, true)
    },

    /**
     * Os campos estão na tela — a deixa de quem põe o cursor no primeiro (`ready`). Ao abrir eles podem
     * esperar a prévia, e o foco dado antes caía num formulário ainda sem campo.
     */
    shown() {
      this.$nextTick(() => this.$emit('ready'))
    },

    choiceOptions(property) {
      if (property.type !== 'choice') return undefined
      const domain = domainOfProperty(property, this.choiceDomains)
      return domain ? domain.options : []
    },

    /**
     * Pergunta ao servidor o que só ele sabe do item como está no formulário — as calculadas, o
     * único e as regras —, pela prévia, e põe as respostas nos campos.
     *
     * Ao SAIR de um campo (o `change`), e não a cada tecla: cada pedido é uma ida ao servidor. Quem
     * calcula e julga as regras é o servidor, com o mesmo motor da leitura e da gravação; e o único
     * depende dos outros itens, que só ele vê. Tudo o mais o formulário confere sozinho
     * (`validate`), sem vir aqui.
     *
     * Só quando o campo que mudou (`changed`) é lido por alguma calculada ou regra, ou é único —
     * mudar o título de uma canção não tem por que pedir a idade de novo. Sem `changed` (o item
     * novo, ao abrir) vão as calculadas sempre: há calculada que não lê campo nenhum, como a de
     * `today`.
     *
     * Só a última resposta vale: sair de dois campos depressa manda dois pedidos, e eles não voltam
     * necessariamente em ordem. E uma falha não trava o formulário — o campo só deixa de acompanhar
     * até a próxima mudança, e o servidor confere tudo de novo ao salvar.
     *
     * `opening`: a prévia da abertura (`start`) — a do item que já existe não mexe no que está gravado.
     * `affecting`: os campos mudados, cujos dependentes ficam travados até ela responder — o `changed`, ou
     * os que a prévia anterior descartou ou desmarcou. Sem nenhum (a abertura), nada trava.
     *
     * E o padrão por fórmula dos campos que ainda o seguem (`defaulted`): a prévia o põe, e é pedida de
     * novo quando muda um campo que ele lê. O campo em que alguém mexeu deixa de segui-lo.
     */
    async askServer(changed, opening = false, affecting = changed ? [changed] : []) {
      if (changed) {
        //Escolher de novo o valor que já estava, ou sair do campo sem mexer, não pede nada (pedido do
        //usuário): a prévia já viu esse valor, e travar o dependente seria à toa
        if (changed in this.asked && JSON.stringify(this.instance[changed]) === this.asked[changed]) return
        this.touched.add(changed)
        //A situação não passa pelo `validateOnInput`: é aqui que a mudança dela chega
        this.forgetRefusal(changed)
      }
      //Mexido — ao digitar já entra no `touched` —, o campo fica com o que a pessoa pôs
      this.defaulted = this.defaulted.filter(alias => !this.touched.has(alias))
      const defaultInputs = new Set(this.idea.properties
          .filter(property => this.defaulted.includes(property.alias))
          .flatMap(property => property.defaultDependsOn || []))
      const computing = this.idea.properties.some(property => property.formula)
          && (!changed || this.formulaInputs.has(changed))
      const judging = !!changed && (this.uniqueFields.has(changed) || this.ruleInputs.has(changed))
      //Ao abrir, para a lista já nascer filtrada; depois, quando muda um campo que um filtro lê
      const filtering = this.filteredFields.size > 0 && (!changed || this.filterInputs.has(changed))
      const defaulting = this.defaulted.length > 0 && (!changed || defaultInputs.has(changed))
      //O asterisco do "Obrigatório quando": ao abrir o item novo, que ainda não o tem da leitura, e quando
      //muda um campo que a condição lê
      const requiring = this.conditionallyRequired.length > 0 && (!changed || this.requiredInputs.has(changed))
      //Com quem vê e quem edita algum campo por fórmula: ao criar, é a prévia que diz o que já nasce
      //oculto ou travado
      if (!computing && !judging && !filtering && !defaulting && !requiring && !this.fieldAccess) return
      //Vai tudo o que esta edição mexeu: o servidor parte do item gravado, e o único e as regras
      //conferem o item como ESTÁ no formulário — uma regra lê mais de um campo. E o que as condições do
      //obrigatório leem: no item novo, o semeado (a situação inicial, o padrão) não foi mexido por ninguém
      const aliases = new Set([...(computing ? this.formulaInputs : []), ...(defaulting ? defaultInputs : []),
        ...(requiring ? this.requiredInputs : []), ...this.touched])
      const draft = Object.fromEntries([...aliases]
          .filter(alias => alias in this.instance)
          .map(alias => [alias, this.instance[alias]]))
      //Trava o que esta prévia vai mudar, até ela responder (pedido do usuário) — ver o `affectedBy`
      this.pending = [...new Set(affecting.flatMap(alias => this.affectedBy(alias)))]
      this.remember()
      const ticket = ++this.previewTicket
      try {
        const {computed, defaults, failures, allowed, access, required, computing} = await this.$instances.preview({
          systemAlias: this.context.system, ideaAlias: this.idea.alias, instanceId: this.context.instance
        }, draft, defaulting ? this.defaulted : [])
        if (ticket !== this.previewTicket) return
        //As calculadas, e o "Calculado quando" ligando e desligando: o preenchido sai e volta ao campo
        applyComputing(this.instance, this.idea.properties, {computing: computing || [], computed})
        //Só no que ainda segue o padrão: quem mexeu no campo enquanto a prévia ia, ficou com o seu. E o
        //padrão é o que a prévia viu (`asked`): sair do campo sem mexer não o tira do padrão. Ao criar, é
        //também o valor de partida (`original`), como o fixo: a janela não fica "suja" por ele, e é para
        //ele que o campo volta quando a prévia o esconde
        for (const [alias, value] of Object.entries(defaults || {})) {
          if (!this.defaulted.includes(alias) || this.touched.has(alias)) continue
          this.instance[alias] = value === null ? undefined : value
          this.asked[alias] = JSON.stringify(this.instance[alias])
          if (!this.context.instance) this.original[alias] = value === null ? undefined : deepCopy(value)
        }
        //Os campos restritos do item como está no formulário (`fieldVisible`/`fieldEditable` os leem):
        //só eles — se o ITEM ainda se edita depois de gravar é a pergunta do aviso de perder o acesso
        if (access) this.instance._access = {...this.instance._access, hidden: access.hidden, locked: access.locked}
        if (required) this.applyRequired(required)
        this.serverMessages = this.messagesFor(failures)
        this.allowed = allowed || {}
        //O valor descartado ou desmarcado pode ser lido por uma calculada, uma regra ou um filtro: a
        //prévia de novo, sem ele. Não gira: os valores só encolhem, e na seguinte nada mais sai
        const settled = [
          ...(access ? this.discardHidden(access.hidden || []) : []),
          ...(opening && this.context.instance ? [] : this.dropDisallowed())
        ]
        if (settled.length) this.askServer(undefined, false, settled)
      } catch (err) {
        console.warn('Não foi possível calcular a prévia', err)
      } finally {
        //Mesmo sem resposta: o formulário não fica preso — o servidor confere tudo ao salvar. Só a última
        //destrava: a anterior, respondendo depois, não solta o que a nova travou
        if (ticket === this.previewTicket) this.pending = []
        if (ticket === this.previewTicket && this.waiting) {
          this.waiting = false
          this.shown()
        }
      }
    },

    /**
     * Os campos do "Obrigatório quando" que a prévia diz obrigatórios agora (`isRequired` os lê): o asterisco
     * aparece e some enquanto se mexe. O campo que passou a ser — ou deixou de ser — obrigatório é conferido
     * de novo, se alguém já mexeu nele ou se já mostrava erro: a frase de obrigatório vem e vai com ele. O
     * intocado espera o salvar, como os outros, e o formulário não se acende sozinho.
     */
    applyRequired(required) {
      const before = asArray(this.instance._required)
      this.instance._required = required
      this.conditionallyRequired
          .filter(property => before.includes(property.alias) !== required.includes(property.alias))
          .filter(property => this.touched.has(property.alias) || this.errorMessages[property.alias])
          .forEach(property => this.validate(property))
    },

    /**
     * Guarda o valor de cada campo como a prévia o vê agora (em texto, para comparar lista e objeto): o
     * `askServer` de um campo que volta igual não vai ao servidor. Ao abrir, o ponto de partida.
     */
    remember() {
      this.asked = Object.fromEntries([...this.idea.properties.map(property => property.alias), '_state']
          .map(alias => [alias, JSON.stringify(this.instance[alias])]))
    },

    /**
     * Os campos que a prévia vai mudar quando muda `changed`: a calculada que o lê (`dependsOn`, que o
     * servidor deriva da fórmula, já pelas outras calculadas) e o campo cujas "Opções permitidas" o leem
     * (`optionFilterDependsOn`), e o que ainda segue o padrão por fórmula que o lê (`defaultDependsOn`).
     * Ficam travados, com o progresso do próprio campo e sem o valor velho, até ela responder (pedido do
     * usuário) — senão se lia um total que já mudou, ou se escolhia uma opção que o filtro novo ia
     * desmarcar.
     */
    affectedBy(changed) {
      return this.idea.properties
          .filter(property => (property.formula && (property.dependsOn || []).includes(changed))
              || (property.optionFilter && !property.formula && (property.optionFilterDependsOn || []).includes(changed))
              || (this.defaulted.includes(property.alias) && (property.defaultDependsOn || []).includes(changed)))
          .map(property => property.alias)
    },

    /**
     * O campo que a prévia escondeu volta ao valor de partida (`original`), e o que se digitou nele é
     * descartado (pedido do usuário): ao reaparecer, ele não traz de volta o que ninguém via. Ao criar,
     * o de partida é o vazio, ou o padrão e o dado por quem abriu; ao editar, o gravado — que o servidor
     * nem manda quando o campo é oculto para quem pede.
     *
     * Sai também dos mexidos e dos erros: não vai na prévia, nem o salvar o confere — gravar o campo
     * oculto seria recusado. Devolve os campos cujo valor mudou.
     */
    discardHidden(hidden) {
      const discarded = hidden.filter(alias =>
          JSON.stringify(this.instance[alias]) !== JSON.stringify(this.original[alias]))
      for (const alias of discarded) {
        if (this.original[alias] === undefined) delete this.instance[alias]
        else this.instance[alias] = deepCopy(this.original[alias])
        this.touched.delete(alias)
        this.forgetRefusal(alias)
        this.settle(alias, [])
      }
      return discarded
    },

    /**
     * Desmarca o que as "Opções permitidas" deixaram de oferecer (pedido do usuário): mudar o estado
     * tira a cidade de outro estado. O campo oferece sempre o que já está escolhido (`offered`, no
     * `InstancePropertyInput`) — mas, mudado o campo que o filtro lê, o escolhido seria recusado ao
     * salvar.
     *
     * Não na abertura do item que já existe: o gravado não sai em silêncio só por abrir o formulário.
     * O fixo (`fixedValues`) também fica — não sai nem pela mão. Devolve os campos que mudaram.
     */
    dropDisallowed() {
      const dropped = []
      for (const [alias, ids] of Object.entries(this.allowed)) {
        const property = this.idea.properties.find(candidate => candidate.alias === alias)
        const chosen = asArray(this.instance[alias])
        const fixed = asArray(this.fixedValues[alias]).map(relatedId)
        //Sem id é o item ainda por criar (o reverso), que o filtro não julgou
        const kept = chosen.filter(value => {
          const id = relatedId(value)
          return id === undefined || ids.includes(id) || fixed.includes(id)
        })
        if (!property || kept.length === chosen.length) continue
        this.instance[alias] = travelsAsList(property) ? kept : kept[0]
        this.touched.add(alias)
        dropped.push(alias)
      }
      return dropped
    },

    /**
     * As falhas do servidor que o formulário mostra, por campo: só as que dizem respeito ao que já
     * foi mexido — o que ninguém tocou espera o salvar, e o formulário novo não abre em vermelho.
     *
     * A regra aparece no campo DELA quando algum campo que ela lê foi mexido, mesmo que seja outro:
     * mudar a Data inicial acusa embaixo da Data final.
     */
    messagesFor(failures) {
      const concerns = failure => failure.validation === 'rule'
          ? (this.idea.properties.find(property => property.alias === failure.property)?.rules || [])
              .some(rule => (rule.dependsOn || []).some(alias => this.touched.has(alias)))
          : this.touched.has(failure.property)
      const messages = {}
      for (const failure of failures.filter(concerns)) {
        if (!messages[failure.property]) messages[failure.property] = failureText(failure, this.idea)
      }
      return messages
    },

    /** A frase embaixo do campo: a da conferência daqui, a da recusa do salvar, e senão a da prévia. */
    messageOf(alias) {
      return this.errorMessages[alias] || this.refusedMessages[alias] || this.serverMessages[alias] || ''
    },

    /**
     * O que o item, como estava gravado, já violava neste campo — o aviso de quem pode consertar, e
     * não precisa (pedido do usuário). A catraca não cobra o campo que ninguém mexeu, e sem aviso o
     * defeito passava calado; com ele, quem edita outra coisa vê o que está pendente.
     *
     * Só enquanto o campo não é mexido e não tem erro: mexido, quem fala é a conferência de verdade —
     * a que barra —, e o aviso de antes não vale mais.
     */
    warningsOf(alias) {
      if (this.touched.has(alias) || this.messageOf(alias)) return []
      return failuresAt(this.instance, alias)
    },

    //Expostos ao template: função de módulo não é enxergada de lá
    isRequired,
    computingNow,
    fieldVisible,
    fieldEditable,

    /**
     * Confere um campo ao sair dele, pelo que o cliente sabe sozinho (`valueFailures`) — na hora, sem
     * ida ao servidor. O único, que depende dos outros itens, vem depois, pela prévia (`askServer`).
     *
     * As validações eram métodos deste componente, e só este formulário as tinha: o módulo é o mesmo
     * para quem mais precisar, e o par dele no servidor é a garantia ao gravar.
     */
    validate(property, value = this.instance[property.alias]) {
      return this.settle(property.alias, valueFailures(property, value, this.instance))
    },

    /**
     * O mesmo enquanto se digita, menos a URL: acusar endereço inválido a cada tecla de um endereço
     * que ainda está sendo escrito só atrapalha — ela é conferida ao sair do campo.
     */
    validateOnInput(property, value = this.instance[property.alias]) {
      this.forgetRefusal(property.alias)
      this.settle(property.alias, valueFailures(property, value, this.instance)
          .filter(failure => failure.validation !== 'url'))
    },

    /** Mostra a primeira falha do campo, ou limpa a que havia. Devolve se o campo está bom. */
    settle(alias, failures) {
      this.errorMessages[alias] = failures.length ? failureText(failures[0], this.idea) : ''
      return !failures.length
    },

    /**
     * Antes de salvar, pelo que o cliente sabe: na criação, todos os campos; na edição, só os
     * mexidos — a CATRACA, a mesma do servidor (decisão do usuário): um obrigatório vazio de antes não
     * impede salvar a mudança de outro campo, e continua com o `*` à vista. Campo já marcado —
     * inclusive pelo único ou por uma regra, que vieram da prévia — impede salvar até ser corrigido.
     *
     * O que só o servidor sabe e a prévia não mostrou, ele confere ao gravar — sem uma prévia a mais
     * antes (decisão do usuário): a recusa volta aos campos pelo `refuse`.
     *
     * A catraca alcança também o "Obrigatório quando" cuja condição leu algo mexido — concluir a tarefa
     * cobra o fim, que ninguém tocou —, como a do servidor (`requiredTouched`).
     */
    validateForm() {
      if (this.idea.properties.some(property => this.messageOf(property.alias))) return false
      const creating = !this.context.instance
      const conditionTouched = p => (p.requiredDependsOn || []).some(alias => this.touched.has(alias))
      return this.idea.properties
          .filter(p => !this.omitProperties.includes(p.alias))
          .filter(p => creating || this.touched.has(p.alias) || conditionTouched(p))
          .map(p => this.validate(p))
          .every(valid => valid)
    },

    /**
     * Uma recusa do servidor ao salvar, onde ela deve aparecer.
     *
     * As falhas de campo (`fields`) vão para os campos, com as mesmas frases da conferência daqui —
     * inclusive as do lado B, embaixo do campo daqui por onde a gravação chegou lá (`item.via`;
     * pedido do usuário: na barra de aviso, longe do campo, não se sabia o que mexer). O resto — outra
     * recusa, ou um erro que não se sabe explicar — vai para a janela de recusa (`refuse`).
     *
     * Um dono para os três hospedeiros (editar, criar na janela e na rota), que tratavam a recusa
     * cada um de um jeito — e um deles não tratava.
     */
    refuse(err) {
      const failures = err && err.data && err.data.validation === 'fields' ? err.data.property : []
      //O campo DAQUI de cada falha: o dela, ou — na do outro lado — o campo por onde a gravação
      //chegou lá (a Parceria que aponta para o compositor)
      const fieldOf = failure => failure.item ? failure.item.via : failure.property
      const shown = failures.filter(failure => this.idea.properties.some(p => p.alias === fieldOf(failure)))
      const refused = {}
      for (const failure of shown) {
        if (!refused[fieldOf(failure)]) refused[fieldOf(failure)] = failureText(failure, this.idea)
      }
      this.refusedMessages = refused
      if (!shown.length || shown.length < failures.length) refuse(err, this.idea)
    },

    /**
     * Esquece a recusa do salvar que a mudança de `changed` pode ter resolvido: a do próprio campo, a de
     * todo campo cuja regra o lê, e a do campo cujo "Obrigatório quando" o lê — voltar o "obrigatório" para
     * Não deixava presa a recusa da descrição, e ela barrava o salvar (relato do usuário). A recusa julgou o
     * item como estava ao salvar; mexido algo de que ela dependia, não vale mais — e quem diz se ainda
     * falha é a conferência daqui ou a prévia.
     *
     * Antes a recusa ia para os erros da conferência daqui (`errorMessages`), que só se apagam
     * conferindo o PRÓPRIO campo. A regra "para encerrar, informe a data" é da data e lê a situação:
     * trocar a situação de volta não tocava a data, e o erro ficava — barrando o salvar (relato do
     * usuário).
     */
    forgetRefusal(changed) {
      const reads = alias => {
        const property = this.idea.properties.find(p => p.alias === alias)
        return alias === changed || (property?.requiredDependsOn || []).includes(changed)
            || (property?.rules || []).some(rule => (rule.dependsOn || []).includes(changed))
      }
      this.refusedMessages = Object.fromEntries(Object.entries(this.refusedMessages).filter(([alias]) => !reads(alias)))
    },

    /** Instâncias que o usuário montou mas que só podem ser criadas depois
     *  que esta existir. Coletado pelos refs dos campos. */
    pendingReverse() {
      const pending = []
      for (const property of this.idea.properties) {
        const input = asElement(this.$refs[`property-${property.alias}`])
        const items = input && input.pendingItems && input.pendingItems()
        if (items && items.length) pending.push(...items)
      }
      return pending
    },

    /**
     * Largura da coluna sob medida, e o lado que se ajusta depende do campo.
     *
     * Os dois usam a mesma faixa — nunca menos de 14em, para o campo continuar legível
     * mesmo vazio, e nunca mais de 30em, para um valor longo não empurrar todo o resto
     * para baixo. Os `ch` são medida de caractere, então "caber o texto" é literal.
     *
     * PISO, nas colunas `auto`: elas dimensionam pelo conteúdo, e o campo de texto
     * aceita encolher até cortar o que está escrito — foi o "Nome" espremido entre a
     * caixa de seleção e a foto. Com um piso, a linha QUEBRA em vez de espremer.
     *
     * TETO, no select de relacionamento: ele tem fração de grade fixa (um terço em
     * telas largas) e ocupava tudo isso mesmo quando as opções são curtas. Como a
     * lista de opções é conhecida, o teto sai do maior identificador do domínio.
     *
     * Fora dos dois: o número, que já tem largura exata pelo numberWidth, e o
     * sim/não, que são dois botões e ficariam com espaço vazio à direita — calculado ou não,
     * porque o calculado usa os mesmos botões, travados.
     *
     * Mas não o número CALCULADO: ele não é o number-input, e sim um campo de texto somente
     * leitura, sem largura própria. Fora da faixa, a coluna dele encolhia até o rótulo virar
     * "M…" e o valor não ter onde aparecer.
     *
     * Vai no style da COLUNA e não no campo: o `.v-col` do Vuetify 4 tem min-width
     * própria, e um mínimo só no campo transbordaria a coluna em vez de quebrar a linha.
     */
    sizeForProperty(property) {
      if (!property || property.type === 'boolean' || (!property.formula && property.type === 'number')) return null

      const band = chars => `clamp(14em, ${chars + 4}ch, 30em)`

      const relationship = property.relationship
      if (relationship && !relationship.reverse && !property.multiple) {
        const longest = this.domainWidths[relationship.idea]
        //Mais a busca, que o autocomplete mantém ao lado do valor escolhido (`min-width: 64px`, uns 7ch), e o
        //"+" de criar, dentro do campo: sem os dois o valor cortava em "RJ · Rio de Jan…" (relato do
        //usuário). A calculada não cria, e não tem o "+"
        const room = SEARCH_CHARS + (property.formula ? 0 : CREATE_BUTTON_CHARS)
        return longest ? {maxWidth: band(longest + room)} : null
      }

      if (this.colsForProperty(property, 6, 6) !== 'auto') return null
      const value = this.instance[property.alias]
      const text = value === null || value === undefined ? '' : String(value)
      return {minWidth: band(Math.max(text.length, (property.name || '').length))}
    },

    /**
     * Coluna que cresce. A do número não: o campo tem largura fixa, medida em
     * caracteres, e a coluna esticada deixava o resto do espaço vazio à direita. O
     * que sobra vai para as outras colunas, que usam.
     */
    growsForProperty(property) {
      return !property || property.multiple || property.type !== 'number'
    },

    colsForProperty(property, multiple, relationship) {
      if (!property) return 'auto'
      //O markdown na largura toda: é um editor com barra, e não um campo — o padrão dele faz o mesmo
      if (displayKind(property) === 'markdown') return 12
      //reversa não carrega flag multiple, mas quase sempre é lista
      if (property.relationship && property.relationship.reverse) return multiple;
      if (property.multiple) return multiple;
      if (property.relationship) return relationship;
      return 'auto';
    },

    focusNext($event) {
      const elem = $event.target
      const currentIndex = Array.from(elem.form.elements).indexOf(elem);
      elem.form.elements.item(
          currentIndex < elem.form.elements.length - 1 ?
              currentIndex + 1 :
              0
      ).focus();
    },

  }
}
</script>
